/**
 * ActiveFiltersChips Component
 * Created: 08-10-2025 11:27:08
 * Purpose: Display active filters as removable chips below toolbar
 * 
 * Features:
 * - Shows all active filters as colored chips
 * - Each chip has remove (X) button
 * - "Clear All" button when multiple filters active
 * - Color-coded by filter type
 * - Smooth fade-in animations
 * - Accessible with keyboard navigation
 * 
 * @example
 * <ActiveFiltersChips
 *   filters={activeFilters}
 *   onRemove={(id) => removeFilter(id)}
 *   onClearAll={() => clearAllFilters()}
 * />
 */

import React from 'react';
import { Group, Text, ActionIcon, Box, Tooltip } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { FilterChip } from '../../types/planner';
import { RemovableChip } from '../atoms/RemovableChip';

interface ActiveFiltersChipsProps {
  /**
   * Array of active filter chips to display
   */
  filters: FilterChip[];
  
  /**
   * Callback when a single filter is removed
   */
  onRemove: (filterId: string) => void;
  
  /**
   * Callback when "Clear All" is clicked
   */
  onClearAll: () => void;
  
  /**
   * Show clear all button
   * @default true
   */
  showClearAll?: boolean;
  
  /**
   * Maximum number of chips to display before showing "+N more"
   * @default undefined (show all)
   */
  maxDisplay?: number;
}

export const ActiveFiltersChips: React.FC<ActiveFiltersChipsProps> = ({
  filters,
  onRemove,
  onClearAll,
  showClearAll = true,
  maxDisplay,
}) => {
  // Don't render if no filters
  if (filters.length === 0) {
    return null;
  }
  
  const displayFilters = maxDisplay ? filters.slice(0, maxDisplay) : filters;
  const hiddenCount = maxDisplay && filters.length > maxDisplay ? filters.length - maxDisplay : 0;
  
  return (
    <Box
      style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
      }}
      role="region"
      aria-label="Active filters"
    >
      <Group gap="xs" align="center">
        <Text size="sm" fw={600} c="dark">
          Active Filters:
        </Text>
        
        {displayFilters.map((filter) => (
          <RemovableChip
            key={filter.id}
            label={filter.label}
            type={filter.type}
            onRemove={() => onRemove(filter.id)}
            color={filter.color}
            size="sm"
          />
        ))}
        
        {hiddenCount > 0 && (
          <Tooltip label={`${hiddenCount} more filters applied`} position="top" withArrow>
            <Text size="xs" c="dimmed" fw={500}>
              +{hiddenCount} more
            </Text>
          </Tooltip>
        )}
        
        {showClearAll && filters.length > 0 && (
          <Tooltip label="Remove all active filters" position="top" withArrow>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={onClearAll}
              style={{
                marginLeft: '8px',
                transition: 'all 200ms ease',
              }}
              aria-label="Clear all filters"
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
};

export default ActiveFiltersChips;

